<template>
  <div v-if="parentForm" :key="parentForm.updated_at" class="px-6">
    <div
      class="sticky top-34 z-10 flex items-center justify-between bg-pure-white pt-4 pb-3 cursor-pointer"
      @click="movingSection = !movingSection"
    >
      <BlTextBody2 :text="`Showing ${numberOfBlocks} block(s)`" />
      <div>
        <BlIcon
          icon="chevron-up"
          class="w-5 h-5 transition-all duration-300"
          :style="{ rotate: movingSection ? '180deg' : '' }"
        />
      </div>
    </div>
    <draggable
      handle=".handle_section"
      @update.prevent="onMoveSection($event)"
      class="flex flex-col gap-2"
    >
      <div
        v-for="section in parentFormSections"
        :key="section.id"
        :id="section.id"
      >
        <button
          class="mb-2 py-1 px-2 w-full text-left flex justify-between gap-2 rounded border border-slate-400 cursor-default outline-none focus:border-light-purple"
        >
          <div
            class="w-full flex gap-2 cursor-pointer"
            @click="onScrollTo(`section_field_${section.id}`)"
          >
            <div v-if="movingSection" class="handle_section pt-1 cursor-grab">
              <BlIcon icon="drag" class="h-4 w-4" />
            </div>
            <div
              class="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded bg-red-salmon"
            >
              <BlIcon
                icon="rectangle-dashed"
                color="white"
                class="w-4 h-4"
                tooltip="Section"
              />
            </div>
            <BlTextSubtitle3 :text="section.name" class="pt-0.5" />
          </div>
          <div class="relative flex-shrink-0">
            <BlInputButton
              @clicked="menuOpened = 'section_' + section.id"
              icon="dots-vertical"
              type="plain"
              class="hover:bg-hover-state w-6 h-6 flex items-center justify-center"
            />
            <div
              v-if="menuOpened === 'section_' + section.id"
              class="absolute mr-4 top-0 right-full flex flex-col gap-1 bg-pure-white rounded border border-slate-400 p-2"
              :style="{ zIndex: 1 }"
            >
              <button
                class="px-4 py-2 text-left min-w-48 outline-none rounded transition-all duration-300"
                :class="[
                  section.questions.length > 0
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-hover-state'
                ]"
                :disabled="section.questions.length > 0"
                @click="onRemoveSection(section.id)"
              >
                <BlTextBody2 class="text-error-400" text="Delete section" />
              </button>
            </div>
          </div>
        </button>
        <div v-if="movingSection" class="pl-12.5">
          <div
            class="mt-2 py-1 px-2 flex items-center gap-2 rounded border border-slate-400"
          >
            <BlTextSubtitle3 :text="`${section.questions.length} block(s)`" />
          </div>
        </div>
        <draggable
          v-else
          :handle="`.handle_question_${section.id}`"
          @update.prevent="onMoveQuestion($event)"
          class="flex flex-col gap-2 mt-2 transition-all duration-300"
        >
          <div
            v-for="question in section.questions"
            :key="question.id"
            :id="`${section.id}_${question.id}`"
            class="pl-12.5"
          >
            <button
              class="py-1 px-2 w-full text-left flex justify-between gap-2 rounded border border-slate-400 outline-none cursor-default focus:border-light-purple"
            >
              <div
                class="w-full flex gap-2 cursor-pointer"
                @click="onScrollTo(`question_field_${question.id}`)"
              >
                <div
                  :class="`handle_question_${section.id}`"
                  class="pt-1 cursor-grab"
                >
                  <BlIcon icon="drag" class="h-4 w-4" />
                </div>
                <div
                  class="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded bg-lime-500"
                >
                  <BlIcon
                    :icon="iconInputs[question.input_type]"
                    color="white"
                    class="w-4 h-4"
                    :tooltip="tootipInputs[question.input_type]"
                  />
                </div>
                <BlTextSubtitle3 :text="question.label" class="pt-0.5" />
              </div>
              <div class="relative flex-shrink-0">
                <BlInputButton
                  @clicked="menuOpened = 'question_' + question.id"
                  icon="dots-vertical"
                  type="plain"
                  class="hover:bg-hover-state w-6 h-6 flex items-center justify-center"
                />
                <div
                  v-if="menuOpened === 'question_' + question.id"
                  class="absolute mr-4 top-0 right-full flex flex-col gap-1 bg-pure-white rounded border border-slate-400 p-2"
                  :style="{ zIndex: 1 }"
                >
                  <div
                    class="px-4 pt-1 pb-2 text-left min-w-64 border-b border-slate-400"
                  >
                    <BlTextBody2
                      class="text-slate-1000 font-bold"
                      text="Move to section:"
                    />
                  </div>
                  <button
                    v-for="(item, idx) in parentFormSections"
                    :key="item.id"
                    class="px-4 py-2 text-left min-w-64 outline-none rounded transition-all duration-300"
                    :class="[
                      item.id === section.id
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-hover-state'
                    ]"
                    :disabled="item.id === section.id"
                    @click="onMoveToSection(question.id, item.id)"
                  >
                    <BlTextBody2
                      class="text-slate-1000"
                      :text="`${idx + 1}. ${item.name}`"
                    />
                  </button>
                </div>
              </div>
            </button>
          </div>
        </draggable>
      </div>
    </draggable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import draggable from "vuedraggable";

import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlIcon from "../../../BlIcon.vue";
import BlInputButton from "../../../BlInputButton.vue";

export default {
  name: "ParentFormPanel",
  components: {
    draggable,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputButton
  },
  data() {
    return {
      saveLoading: false,
      movingSection: false,
      menuOpened: ""
    };
  },
  computed: {
    ...mapGetters(["currentUser", "parentForm", "parentFormSections"]),
    numberOfBlocks() {
      let n = 0;
      this.parentFormSections.forEach(i => {
        const q = i.questions.length;
        n += q;
      });
      return n;
    },
    iconInputs() {
      return {
        text: "short-answer",
        textarea: "long-paragraph",
        date: "calendar-blank",
        radio: "radio-button",
        checkbox: "to-do"
      };
    },
    tootipInputs() {
      return {
        text: "Short answer",
        textarea: "Paragraph",
        date: "Date",
        radio: "Multiple choice",
        checkbox: "Checkboxes"
      };
    }
  },
  watch: {
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  mounted() {},
  methods: {
    ...mapActions(["updateParentFormSection", "deleteParentFormSection", "updateParentFormQestion"]),
    onScrollTo(id) {
      // const body = document.getElementById("form-body");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    onMoveSection(event) {
      const id = event.item.id;
      const sectionId = Number(id);
      const section = this.parentFormSections.find(i => i.id === sectionId);
      if (!section) return;
      const newPosition = event.newIndex + 1;
      if (section.position === newPosition) return;

      const data = {
        id: section.id,
        section: { position: newPosition }
      };
      this.saveLoading = true;
      this.updateParentFormSection(data).then(({ success }) => {
        this.saveLoading = false;
        // if (!success) return;
      });
    },
    onMoveQuestion(event) {
      const id = event.item.id.split("_");
      const sectionId = Number(id[0]);
      const questionId = Number(id[1]);
      const section = this.parentFormSections.find(i => i.id === sectionId);
      if (!section) return;
      const question = section.questions.find(i => i.id === questionId);
      if (!question) return;
      const newPosition = event.newIndex + 1;
      if (question.position === newPosition) return;

      const data = {
        id: question.id,
        question: { position: newPosition }
      };
      this.saveLoading = true;
      this.updateParentFormQestion(data).then(({ success }) => {
        this.saveLoading = false;
        // if (!success) return;
      });
    },
    onMoveToSection(questionId, sectionId) {
      const data = {
        id: questionId,
        question: { section_id: sectionId }
      };
      this.saveLoading = true;
      this.updateParentFormQestion(data).then(({ success }) => {
        this.saveLoading = false;
        // if (!success) return;
      });
    },
    onRemoveSection(sectionId) {
      this.saveLoading = true;
      this.deleteParentFormSection({ id: sectionId }).then(({ success }) => {
        this.saveLoading = false;
        // if (!success) return;
      });
    },
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.menuOpened = "";
    }
  }
};
</script>

<style scoped lang="scss">
.form-base {
  height: calc(100vh - 4.5rem);
  .name-input {
    width: 32rem;
    height: 2.5rem;
    padding: 4px 8px 4px 8px;
    border-radius: 5px;
    border: 1px solid var(--Slate-5, #d0d5dd);
    //
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 2rem;
    letter-spacing: 0.5px;
    text-align: left;
    color: var(--Slate-10, #1d2939);
    //
    outline: none;
  }
  .description-input {
    width: 32rem;
    height: 6.25rem;
    padding: 4px 8px 4px 8px;
    border-radius: 5px;
    border: 1px solid var(--Slate-5, #d0d5dd);
    //
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.375rem;
    letter-spacing: 0.5px;
    text-align: left;
    color: var(--Slate-7, #475467);
    //
    outline: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/center/salesPipeline/parentForm/Panel.vue